import { supabase } from "@/lib/supabase";
import type {
  ExtractionResponse,
  ExtractionError,
} from "@/types/extraction";

const STORAGE_BUCKET = "dec-pages";
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

export const SUPPORTED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;

export const ACCEPT_ATTRIBUTE =
  ".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,image/*";

export type ClientUploadError = {
  ok: false;
  error: string;
  code:
    | "no_session"
    | "file_too_large"
    | "invalid_file_type"
    | "upload_failed"
    | "network_error";
};

export type ClientExtractionResult = ExtractionResponse | ExtractionError | ClientUploadError;

function extensionFor(mimeType: string): string {
  switch (mimeType) {
    case "application/pdf":
      return "pdf";
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "image/heic":
      return "heic";
    case "image/heif":
      return "heif";
    default:
      return "bin";
  }
}

function validateFile(file: File): ClientUploadError | null {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      ok: false,
      error: "File is too large. Maximum size is 20MB.",
      code: "file_too_large",
    };
  }
  const okByMime = (SUPPORTED_FILE_TYPES as readonly string[]).includes(file.type);
  const lowerName = file.name.toLowerCase();
  const okByExtension = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".heic", ".heif"].some(
    (ext) => lowerName.endsWith(ext),
  );
  if (!okByMime && !okByExtension) {
    return {
      ok: false,
      error: "We support PDF, PNG, JPEG, WEBP, and HEIC files.",
      code: "invalid_file_type",
    };
  }
  return null;
}

/**
 * Upload a dec page and run extraction. Two-step flow:
 * 1. Upload file to Supabase storage (RLS enforced by user path prefix)
 * 2. POST storage path to /api/extract-dec-page
 *
 * The caller passes an onProgress callback that receives the current stage
 * ("uploading" | "reading") so the UI can update its progress indicator.
 */
export async function uploadAndExtract(
  file: File,
  onProgress?: (stage: "uploading" | "reading") => void,
): Promise<ClientExtractionResult> {
  const validation = validateFile(file);
  if (validation) return validation;

  // 1. Session + user id
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id || !session?.access_token) {
    return {
      ok: false,
      error: "Please sign in again and retry.",
      code: "no_session",
    };
  }

  // 2. Upload to storage
  onProgress?.("uploading");
  const ext = extensionFor(file.type);
  const storagePath = `${session.user.id}/${Date.now()}.${ext}`;
  const { error: uploadErr } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, file, {
      contentType: file.type || "application/octet-stream",
      upsert: false,
    });

  if (uploadErr) {
    return {
      ok: false,
      error: uploadErr.message || "Upload failed. Please try again.",
      code: "upload_failed",
    };
  }

  // 3. Call extraction endpoint
  onProgress?.("reading");
  try {
    const response = await fetch("/api/extract-dec-page", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ storagePath }),
    });

    const json = (await response.json()) as ExtractionResponse | ExtractionError;
    return json;
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Network error",
      code: "network_error",
    };
  }
}
